// import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { BUILDING, BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import { COCLASS, COCLASSES } from '@/interfaces/coclass.interface'
import { COMPLEX, COMPLEX_PROPERTIES } from '@/interfaces/complex.interface'
import { LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { REAL_ESTATE, REAL_ESTATE_PROPERTIES } from '@/interfaces/realEstate.interface'
import { ROOM_CAPACITY_FILTER } from '@/interfaces/roomcapacity.interface'
import { ROOM_PROPERTIES } from '@/interfaces/room.interface'
import { TimeType } from '@/utils/calendar-utils'
import { Geometry } from '@turf/turf'
import { InjectionKey } from 'vue'

export type Nullable<T> = T | null
export type Undefineable<T> = T | undefined

export type MapClick = 'region' | 'complex' | 'realEstate' | 'building' | 'level' | 'room'
export type QuickSelectStart = 'yesterday' | 'now'

export type QuickSelect = {
  title: LocaleString
  name: QuickSelectType
  start: QuickSelectStart
  unit: TimeType
  steps: number
}
export const QUICK_SELECTS = [
  {
    name: 'yesterday',
    title: {
      en: 'Yesterday',
      sv: 'Igår'
    },
    start: 'yesterday',
    unit: 'day',
    steps: 0
  },
  {
    name: 'last7days',
    title: {
      en: 'Last 7 days',
      sv: 'Senaste 7 dagarna'
    },
    start: 'yesterday',
    unit: 'day',
    steps: 6
  },
  {
    name: 'last30days',
    title: {
      en: 'Last 30 days',
      sv: 'Senaste 30 dagarna'
    },
    start: 'yesterday',
    unit: 'day',
    steps: 29
  },
  {
    name: 'previousQuarter',
    title: {
      en: 'Previous quarter',
      sv: 'Föregående kvartal'
    },
    start: 'now',
    unit: 'quarter',
    steps: 1
  },
  {
    name: 'last12months',
    title: {
      en: 'Last 12 months',
      sv: 'Senaste 12 månaderna'
    },
    start: 'now',
    unit: 'month',
    steps: 12
  },
  {
    name: 'currentYear',
    title: {
      en: 'Current year',
      sv: 'Innevarande år'
    },
    start: 'yesterday',
    unit: 'year',
    steps: 0
  },
  {
    name: 'previousYear',
    title: {
      en: 'Previous year',
      sv: 'Föregående år'
    },
    start: 'now',
    unit: 'year',
    steps: 1
  }
] as const

export type QuickSelectType = (typeof QUICK_SELECTS)[number]['name']

export type RoomCapacity = ROOM_CAPACITY_FILTER
export type LevelProperties = LEVEL_PROPERTIES
export type BuildingFeature = BUILDING
export type BuildingProperties = BUILDING_PROPERTIES & { geometry?: Geometry }
export type ComplexFeature = COMPLEX
export type ComplexProperties = COMPLEX_PROPERTIES
export type RealEstateProperties = REAL_ESTATE_PROPERTIES
export type RealEstateFeature = REAL_ESTATE
export type RoomProperties = ROOM_PROPERTIES
export type RoomType = COCLASS
export type CardState = 'ACTIVE' | 'LOADING' | 'ERROR' | 'DISABLED' | 'EMPTY'

export type RoomTypeFilter = {
  coclass?: string
  roomGroup?: string
}

export function roomTypeFiltersToCoclasses(roomTypeFilter: RoomTypeFilter[]): string[] {
  return [...new Set(roomTypeFilter.flatMap((filter) => roomTypeFilterToCoclasses(filter)))]
}

export function roomTypeFilterToCoclasses(roomTypeFilter: RoomTypeFilter): string[] {
  if (roomTypeFilter.coclass) {
    return [roomTypeFilter.coclass]
  } else if (roomTypeFilter.roomGroup) {
    return COCLASSES.filter((roomType) => roomType.roomGroup === roomTypeFilter.roomGroup).map(
      (roomType) => roomType.coclass
    )
  }
  return []
}

export function roomTypeFiltersToRoomType(roomTypeFilter: RoomTypeFilter[]): RoomType[] {
  return [...new Set(roomTypeFilter.flatMap((filter) => roomTypeFilterToRoomType(filter)))]
}

export function roomTypeFilterToRoomType(roomTypeFilter: RoomTypeFilter): RoomType[] {
  if (roomTypeFilter.coclass) {
    return COCLASSES.filter((roomType) => roomType.coclass === roomTypeFilter.coclass)
  } else if (roomTypeFilter.roomGroup) {
    return COCLASSES.filter((roomType) => roomType.roomGroup === roomTypeFilter.roomGroup)
  }
  return []
}

export type FilterState = {
  dtFromFilter: Undefineable<string>
  dtToFilter: Undefineable<string>
  complexFilter: Nullable<ComplexProperties>
  realEstateFilter: Nullable<RealEstateProperties>
  buildingFilter: BuildingProperties[]
  levelFilter: Nullable<LevelProperties>
  roomTypeFilter: Array<RoomType>
  roomCapacityFilter: Array<RoomCapacity>
  bookableFilter: boolean
  buisinessDaysFilter: boolean
  buisinessHoursFilter: boolean
  quickSelectFilter: Nullable<QuickSelect>
  yearFilter: Undefineable<number>
}

export interface SharedData {
  fullscreen: boolean
  sectionIndex: number
  updateSectionIndex: (index: number) => void
}

export const sharedDataKey: InjectionKey<SharedData> = Symbol()

export type LocaleString = {
  [key in string]: string
}
